import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Input, InputRef, Space, Table } from 'antd';
import React, { useState } from 'react';
import Highlighter from 'react-highlight-words';
import { getClient } from '../../apollo';
import {
  DrivingCompany,
  GetAllDrivingCompaniesListQuery,
  useGetAllDrivingCompaniesListQuery,
} from '../../graphql/datamodel.gen';
import { getTableScroll } from '../../utils/table';
import { Manage } from './manage';

type DrivingCompaniesProps = {};

export const DrivingCompanies: React.FC<DrivingCompaniesProps> = () => {
  const drivingCompaniesQuery = useGetAllDrivingCompaniesListQuery({
    client: getClient(),
    fetchPolicy: 'cache-and-network',
  });
  const searchInputRef = React.useRef<InputRef>(null);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState();
  const [selectedDrivingCompany, setSelectedDrivingCompany] = useState<
    GetAllDrivingCompaniesListQuery['drivingCompanies'][0] | null
  >(null);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  const handleReset = (clearFilters, confirm) => {
    clearFilters();
    confirm();
    setSearchText('');
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInputRef}
          placeholder={`Buscar`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined translate={undefined} />}
            size="small"
            style={{ width: 90 }}
          >
            Buscar
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Limpiar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{ color: filtered ? '#1890ff' : undefined }}
        translate={undefined}
      />
    ),
    onFilter: (value, record) => {
      const searchValue = dataIndex.split('.').reduce(function (a, b) {
        return a[b];
      }, record);

      return searchValue
        ? searchValue.toString().toLowerCase().includes(value.toLowerCase())
        : '';
    },
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInputRef?.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  return (
    <div>
      <Manage
        visible={!!selectedDrivingCompany}
        drivingCompanyId={selectedDrivingCompany?.id}
        onClose={() => {
          setSelectedDrivingCompany(null);
        }}
      />
      <div style={{ marginBottom: '1.5rem' }}>
        <Space>
          <Button
            onClick={() => drivingCompaniesQuery.refetch()}
            icon={<ReloadOutlined translate={undefined} />}
          >
            Refrescar
          </Button>
        </Space>
      </div>
      <Table
        loading={drivingCompaniesQuery.loading}
        sticky
        size="small"
        pagination={{ pageSize: 25 }}
        columns={[
          {
            key: 'name',
            dataIndex: 'name',
            title: 'Nombre',
            ...getColumnSearchProps('name'),
            sorter: (a: DrivingCompany, b: DrivingCompany) => {
              return a.name?.localeCompare(b.name);
            },
            sortDirections: ['ascend', 'descend'],
            defaultSortOrder: 'ascend',
          },
          {
            key: 'phone',
            dataIndex: 'phone',
            title: 'Teléfono',
            ...getColumnSearchProps('phone'),
            sorter: (a: DrivingCompany, b: DrivingCompany) => {
              return a.phone?.localeCompare(b.phone);
            },
            sortDirections: ['ascend', 'descend'],
          },
          {
            key: 'email',
            dataIndex: 'email',
            title: 'Email',
            ...getColumnSearchProps('email'),
            sorter: (a: DrivingCompany, b: DrivingCompany) => {
              return a.email?.localeCompare(b.email);
            },
            sortDirections: ['ascend', 'descend'],
          },
          {
            key: 'actions',
            title: 'Acciones',
            render: (
              _,
              record: GetAllDrivingCompaniesListQuery['drivingCompanies'][0],
            ) => {
              return (
                <Space>
                  <Button
                    size={'small'}
                    onClick={() => {
                      setSelectedDrivingCompany(record);
                    }}
                  >
                    Gestionar
                  </Button>
                </Space>
              );
            },
          },
        ]}
        dataSource={drivingCompaniesQuery.data?.drivingCompanies}
        scroll={{ y: getTableScroll() }}
      />
    </div>
  );
};
